import {messagesService} from "@dv5/api"
import {
    addNewWidget,
    addNewWidgets,
    addNewWidgetsAndLinks,
    addWidgetComment,
    addWidgetLink,
    CanvasContent,
    CanvasSettings,
    CanvasUpdate,
    changeWidgetData,
    changeWidgetLinkData,
    changeWidgetsData,
    cloneLayer,
    ClonedLayer,
    Comment,
    loadContent,
    invalidateWidgetTarget,
    invalidateCanvasContent,
    TargetInvalidation,
    moveWidgets,
    removeWidgetLinks,
    removeWidgets,
    resizeWidgets,
    setWidgetComments,
    updateWidgetComment,
    removeWidgetComment,
    setCanvasSettings,
    Widget,
    WidgetAndLinks,
    WidgetLink, UpdatedCanvasSettings, LoadedCanvasContent
} from '@dv5/domain'
import {ErrorDisplayProcessor} from "@dv5/components"
import {pocketService} from "@dv5/pocket"
import {Dispatch} from "react"

interface GetComments {
    sourceItemId: string
    comments: Array<Comment>
}

class CanvasMessagesService {
    register(dispatch: Dispatch<any>) {
        messagesService.registerService('canvas', {
            processors: {
                'error': (body: {canvasId: string, message: string}) => {
                    new ErrorDisplayProcessor(dispatch).displayError(body.message)
                    // Reconcile optimistic changes rejected after another client enabled read only.
                    dispatch(invalidateCanvasContent({canvasId: body.canvasId}))
                },
                'content': (body: any) => {
                    dispatch(loadContent(body as CanvasUpdate<LoadedCanvasContent>))
                },
                'target-unavailable': (body: any) => {
                    dispatch(invalidateWidgetTarget(body as CanvasUpdate<TargetInvalidation>))
                },
                'new-widget': (body: any) => {
                    dispatch(addNewWidget(body as CanvasUpdate<Widget>))
                },
                'new-widget-link': (body: any) => {
                    dispatch(addWidgetLink(body as CanvasUpdate<WidgetLink>))
                },
                'new-widgets': (body: any) => {
                    dispatch(addNewWidgets(body as CanvasUpdate<Array<Widget>>))
                },
                'created-widgets-from-template': (body: any) => {
                    dispatch(addNewWidgetsAndLinks(body as CanvasUpdate<WidgetAndLinks>))
                },
                'cloned-layer': (body: any) => {
                    dispatch(cloneLayer(body as CanvasUpdate<ClonedLayer>))
                },
                'changed-widget': (body: any) => {
                    dispatch(changeWidgetData(body as CanvasUpdate<Widget>))
                },
                'toggled-widget-locked': (body: any) => {
                    dispatch(changeWidgetData(body as CanvasUpdate<Widget>))
                },
                'changed-widgets': (body: any) => {
                    dispatch(changeWidgetsData(body as CanvasUpdate<Array<Widget>>))
                },
                'moved-widgets': (body: any) => {
                    dispatch(moveWidgets(body as CanvasUpdate<Array<Widget>>))
                },
                'resized-widgets': (body: any) => {
                    dispatch(resizeWidgets(body as CanvasUpdate<Array<Widget>>))
                },
                'removed-widgets': (body: any) => {
                    dispatch(removeWidgets(body as CanvasUpdate<Array<string>>))
                },
                'removed-widget-links': (body: any) => {
                    dispatch(removeWidgetLinks(body as CanvasUpdate<Array<string>>))
                },
                'removed-data-link': (body: any) => {
                    dispatch(changeWidgetData(body as CanvasUpdate<Widget>))
                },
                'changed-widget-link': (body: any) => {
                    dispatch(changeWidgetLinkData(body as CanvasUpdate<WidgetLink>))
                },
                'new-data-link': (body: any) => {
                    pocketService.removeSelectedItem(dispatch)
                    dispatch(changeWidgetData(body as CanvasUpdate<Widget>))
                },
                'new-comment': (body: any) => {
                    const comment = (body as CanvasUpdate<Comment>).changes
                    dispatch(addWidgetComment({sourceItemId: comment.sourceItemId, comment}))
                },
                'widget-comments': (body: any) => {
                    const updatedBody = body as CanvasUpdate<GetComments>
                    const {sourceItemId, comments} = updatedBody.changes
                    dispatch(setWidgetComments({sourceItemId, comments}))
                },
                'edited-widget-comment': (body: any) => {
                    const updatedBody = body as CanvasUpdate<Comment>
                    const comment = updatedBody.changes
                    dispatch(updateWidgetComment({sourceItemId: comment.sourceItemId, comment}))
                },
                'removed-widget-comment': (body: any) => {
                    const {sourceItemId, id} = (body as CanvasUpdate<Comment>).changes
                    dispatch(removeWidgetComment({sourceItemId, commentId: id}))
                },
                'settings': (body: any) => {
                    const {canvasId, changes} = body as CanvasUpdate<CanvasSettings>
                    dispatch(setCanvasSettings({canvasId, changes}))
                },
                'updated-settings': (body: any) => {
                    const {canvasId, changes} = body as CanvasUpdate<UpdatedCanvasSettings>
                    const canvasSettings: CanvasSettings = {
                        alignToGrid: changes.positionSettings.alignToGrid,
                        displayGrid: changes.visualSettings.displayGrid,
                        markdownComments: changes.widgetSettings.markdownComments,
                        readOnly: changes.widgetSettings.readOnly ?? false,
                    }
                    dispatch(setCanvasSettings({canvasId, changes: canvasSettings}))
                },
                // The scroll-position broadcast is received but intentionally ignored:
                // applying it would fight the local viewport state and cause trembling.
                'updated-scroll-position': () => {
                },
                'updated-selected-layers': () => {

                }
            }
        })
    }
}

export const canvasMessagesService = new CanvasMessagesService()
